import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Separator } from '@/shared/components/ui/separator';
import { AiBadge } from '@/shared/components/custom/ai-badge/AiBadge';
import { useConvertToTaskForm } from '../hooks/useConvertToTaskForm';
import { ConvertToTaskIdentitySection } from './ConvertToTaskIdentitySection';
import { ConvertToTaskAssignmentSection } from './ConvertToTaskAssignmentSection';
import { ConvertToTaskScheduleSection } from './ConvertToTaskScheduleSection';

interface ConvertToTaskDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  noteId: string;
  noteTitle: string | null;
  noteContent: string | null;
}

export function ConvertToTaskDialog({
  open,
  onOpenChange,
  noteId,
  noteTitle,
}: ConvertToTaskDialogProps) {
  const { t } = useTranslation('notes');
  const {
    control,
    handleSubmit,
    onSubmit,
    isPending,
    isInterpreting,
    proposal,
    projectId,
    boardId,
    selectedTags,
    setSelectedTags,
    organizationOptions,
    projectCandidates,
    fetchProjects,
    selectedProjectOption,
    projectMatch,
    proposedProject,
    memberOptions,
    assigneeMatch,
    orgMembers,
    boardOptions,
    statusOptions,
    statusMatch,
    groupOptions,
    groupMatch,
    proposedStatus,
    proposedGroup,
    proposedAssignee,
    priorityOptions,
  } = useConvertToTaskForm(noteId, noteTitle, open);

  const submit = handleSubmit(async (data) => {
    const success = await onSubmit(data);
    if (success) onOpenChange(false);
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90vh] gap-0 overflow-y-auto sm:max-w-[560px]"
        data-testid="notes-convert-to-task-dialog"
      >
        <DialogHeader>
          <AiBadge label={t('convertToTask.generatedByAI')} className="w-fit border-none" />
          <DialogTitle className="text-xl">{t('convertToTask.dialogTitle')}</DialogTitle>
          <DialogDescription>{t('convertToTask.dialogDescription')}</DialogDescription>
        </DialogHeader>

        <Separator className="my-5" />

        {isInterpreting && (
          <div className="mb-4 flex items-center gap-2 rounded-md bg-primary/10 px-3 py-2 text-sm text-primary">
            <Loader2 className="size-4 animate-spin" aria-hidden />
            {t('convertToTask.interpreting')}
          </div>
        )}

        <form id="convert-to-task-form" onSubmit={submit} className="flex flex-col gap-5">
          <ConvertToTaskIdentitySection
            control={control}
            isPending={isPending}
            proposal={proposal}
            organizationOptions={organizationOptions}
            fetchProjects={fetchProjects}
            selectedProjectOption={selectedProjectOption}
            projectMatch={projectMatch}
            projectCandidatesCount={projectCandidates.length}
            proposedProject={proposedProject}
          />

          <ConvertToTaskAssignmentSection
            control={control}
            isPending={isPending}
            proposal={proposal}
            projectId={projectId}
            boardId={boardId}
            memberOptions={memberOptions}
            assigneeMatch={assigneeMatch}
            orgMembers={orgMembers}
            boardOptions={boardOptions}
            statusOptions={statusOptions}
            statusMatch={statusMatch}
            groupOptions={groupOptions}
            groupMatch={groupMatch}
            proposedStatus={proposedStatus}
            proposedGroup={proposedGroup}
            proposedAssignee={proposedAssignee}
            priorityOptions={priorityOptions}
          />

          <ConvertToTaskScheduleSection
            control={control}
            isPending={isPending}
            proposal={proposal}
            projectId={projectId}
            selectedTags={selectedTags}
            onTagsChange={setSelectedTags}
          />
        </form>

        <DialogFooter className="mt-6 gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
            data-testid="notes-convert-cancel-button"
          >
            {t('convertToTask.actions.cancel')}
          </Button>
          <Button
            type="submit"
            form="convert-to-task-form"
            disabled={isPending || isInterpreting}
            data-testid="notes-convert-submit-button"
          >
            {isPending && <Loader2 className="mr-2 size-4 animate-spin" aria-hidden />}
            {isPending ? t('convertToTask.actions.creating') : t('convertToTask.actions.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
